import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';

export default function EditPost() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { postId } = useParams();

  useEffect(() => {
    const fetchPost = async () => {
      const docRef = doc(db, 'posts', postId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const postData = docSnap.data();
        if (currentUser.uid === postData.authorId) {
          setTitle(postData.title);
          setContent(postData.content);
        } else {
          setError("You don't have permission to edit this post.");
        }
      } else {
        setError('Post not found.');
      }
    };

    if (currentUser) {
      fetchPost();
    }
  }, [postId, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      return setError('Title and content cannot be empty.');
    }
    setLoading(true);
    setError('');
    try {
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        title,
        content,
        updatedAt: serverTimestamp(),
      });
      navigate('/');
    } catch (err) {
      setError('Failed to update post. Please try again.');
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="glass-card rounded-2xl p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Edit Report</h1>
          <p className="text-gray-400">Update the details of your report below.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block font-semibold mb-3 text-gray-200">Title</label>
            <input 
              type="text" 
              id="title" 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" 
              placeholder="Enter a descriptive title..."
              required
            />
          </div>
          
          <div>
            <label htmlFor="content" className="block font-semibold mb-3 text-gray-200">
              Content
            </label>
            <textarea 
              id="content" 
              rows="8" 
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" 
              placeholder="Provide detailed information..."
              required
            />
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          <div className="pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="w-full glow-button glass-card py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 neon-cyan bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/30 hover:from-cyan-500/30 hover:to-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 className="animate-spin" size={20} />}
              {loading ? 'Updating...' : 'Update Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}